import React, { useEffect, useState } from "react";
import { ResponsiveBar } from "@nivo/bar";
import axios from "axios";
import dayjs from "dayjs";

export default function EnergyClubDashboard() {
  // Chart 1 (Assigned vs Submitted)
  const [barData, setBarData] = useState([]);
  const [activities, setActivities] = useState([]);
  const [subActivities, setSubActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState("");
  const [selectedSubActivity, setSelectedSubActivity] = useState("");
  const [selectedFY1, setSelectedFY1] = useState("");

  // Chart 2 (Schools)
  const [schoolData, setSchoolData] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [clubSearch, setClubSearch] = useState("");
  const [clubLoading, setClubLoading] = useState(false);
  const [selectedClub, setSelectedClub] = useState("");
  const [selectedActivity2, setSelectedActivity2] = useState("");
  const [selectedFY2, setSelectedFY2] = useState("");

  const [loading1, setLoading1] = useState(false);
  const [loading2, setLoading2] = useState(false);

  // ✅ Detect current financial year dynamically
  const getCurrentFinancialYear = () => {
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth() + 1;
    return m >= 4
      ? { label: `${y}-${y + 1}`, start: `${y}-04-01`, end: `${y + 1}-03-31` }
      : { label: `${y - 1}-${y}`, start: `${y - 1}-04-01`, end: `${y}-03-31` };
  };

  const financialYears = [
    { label: "2023-2024", start: "2023-04-01", end: "2024-03-31" },
    { label: "2024-2025", start: "2024-04-01", end: "2025-03-31" },
    { label: "2025-2026", start: "2025-04-01", end: "2026-03-31" },
    { label: "2026-2027", start: "2026-04-01", end: "2027-03-31" },
  ];

  // 🔹 Fetch data lists
  const fetchActivities = async () => {
    try {
      const res = await axios.get("http://192.168.0.57:5000/api/activities");
      setActivities(res.data);
    } catch (err) {
      console.error("❌ Error fetching activities:", err);
    }
  };

  const fetchSubActivities = async (activityId) => {
    try {
      if (!activityId) {
        setSubActivities([]);
        setSelectedSubActivity("");
        return;
      }
      const res = await axios.get("http://192.168.0.57:5000/api/subactivities", {
        params: { activity_id: activityId },
      });
      setSubActivities(res.data);
      setSelectedSubActivity("");
    } catch (err) {
      console.error("❌ Error fetching subactivities:", err);
    }
  };

  // ⚡ Lazy-load school list by search
  const fetchClubs = async (searchTerm = "") => {
    try {
      setClubLoading(true);
      const res = await axios.get("http://192.168.0.57:5000/api/energy-club-list", {
        params: { search: searchTerm },
      });
      setClubs(res.data || []);
    } catch (err) {
      console.error("❌ Error fetching clubs:", err);
    } finally {
      setClubLoading(false);
    }
  };

  // ---------------------- Chart 1: Assigned vs Submitted ----------------------
  const fetchChart1 = async (fyObj = null) => {
    const fy =
      fyObj ||
      financialYears.find((f) => f.label === selectedFY1) ||
      getCurrentFinancialYear();

    setLoading1(true);
    try {
      const res = await axios.get(
        "http://192.168.0.57:5000/api/energy-club-status-financialyear",
        {
          params: {
            club_id: "7723",
            fy_start: fy.start,
            fy_end: fy.end,
            activity_id: selectedActivity || undefined,
            subActivity_id: selectedSubActivity || undefined,
          },
        }
      );

      const grouped = {};
      res.data.forEach((r) => {
        const month = dayjs(r.monthyear + "-01").format("MMM YYYY");
        if (!grouped[month])
          grouped[month] = { month, Assigned: 0, Submitted: 0 };
        grouped[month].Assigned += Number(r.total_assigned || 0);
        grouped[month].Submitted += Number(r.total_submitted || 0);
      });

      setBarData(Object.values(grouped));
    } catch (err) {
      console.error("Error fetching chart 1:", err);
    } finally {
      setLoading1(false);
    }
  };

  // ---------------------- Chart 2: School-wise / All Schools ----------------------
  const fetchChart2 = async (fyObj = null) => {
    const fy =
      fyObj ||
      financialYears.find((f) => f.label === selectedFY2) ||
      getCurrentFinancialYear();

    setLoading2(true);
    try {
      const res = await axios.get(
        "http://192.168.0.57:5000/api/monthly-school-submissions-fy",
        {
          params: {
            fy_start: fy.start,
            fy_end: fy.end,
            club_id: selectedClub || undefined,
            activity_id: selectedActivity2 || undefined,
          },
        }
      );

      let formatted = [];

      if (selectedClub) {
        formatted = res.data.map((r) => ({
          month: r.month,
          "Submitted Activities": Number(r["Submitted Activities"] || 0),
        }));
      } else {
        formatted = res.data.map((r) => ({
          month: r.month,
          "Total Schools": Number(r.total_schools || 0),
          "Submitted Schools": Number(r.submitted_schools || 0),
        }));
      }

      setSchoolData(formatted);
    } catch (err) {
      console.error("Error fetching chart 2:", err);
      setSchoolData([]);
    } finally {
      setLoading2(false);
    }
  };

  // ✅ Initial load
  useEffect(() => {
    const currentFY = getCurrentFinancialYear();
    setSelectedFY1(currentFY.label);
    setSelectedFY2(currentFY.label);
    fetchActivities();
    fetchChart1(currentFY);
    fetchChart2(currentFY);
  }, []);

  useEffect(() => {
    fetchSubActivities(selectedActivity);
  }, [selectedActivity]);

  // Chart configurations
  const barSettings1 = {
    keys: ["Assigned", "Submitted"],
    indexBy: "month",
    groupMode: "grouped",
    colors: ["#FF6384", "#36A2EB"],
    margin: { top: 50, right: 30, bottom: 100, left: 70 },
    padding: 0.3,
    axisBottom: { tickRotation: -35, legend: "Month", legendOffset: 60 },
    axisLeft: { legend: "Total Activities", legendOffset: -50 },
    legends: [
      {
        dataFrom: "keys",
        anchor: "bottom",
        direction: "row",
        translateY: 80,
        itemsSpacing: 10,
        itemWidth: 140,
        itemHeight: 20,
        symbolSize: 18,
      },
    ],
  };

  const barSettings2 = selectedClub
    ? {
        keys: ["Submitted Activities"],
        indexBy: "month",
        colors: ["#4BC0C0"],
        margin: { top: 50, right: 30, bottom: 100, left: 70 },
        padding: 0.3,
        axisBottom: { tickRotation: -35, legend: "Month", legendOffset: 60 },
        axisLeft: { legend: "Activities Submitted", legendOffset: -50 },
      }
    : {
        keys: ["Total Schools", "Submitted Schools"],
        indexBy: "month",
        groupMode: "grouped",
        colors: ["#FFCE56", "#4BC0C0"],
        margin: { top: 50, right: 30, bottom: 100, left: 70 },
        padding: 0.3,
        axisBottom: { tickRotation: -35, legend: "Month", legendOffset: 60 },
        axisLeft: { legend: "Number of Schools", legendOffset: -50 },
      };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "30px",
        background: "#f4f6f8",
        padding: "30px",
        minHeight: "100vh",
      }}
    >
      {/* Chart 1 */}
      <div
        style={{
          background: "#fff",
          borderRadius: "12px",
          padding: "25px",
          boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
        }}
      >
        <h2 style={{ textAlign: "center", color: "#222" }}>
          Assigned vs Submitted (FY {selectedFY1})
        </h2>

        {/* Filters */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "20px" }}>
          <select
            value={selectedFY1}
            onChange={(e) => setSelectedFY1(e.target.value)}
            style={{ flex: 1, padding: "8px" }}
          >
            {financialYears.map((fy) => (
              <option key={fy.label} value={fy.label}>
                {fy.label}
              </option>
            ))}
          </select>

          <select
            value={selectedActivity}
            onChange={(e) => setSelectedActivity(e.target.value)}
            style={{ flex: 1, padding: "8px" }}
          >
            <option value="">All Activities</option>
            {activities.map((a) => (
              <option key={a.activities_id} value={a.activities_id}>
                {a.activity}
              </option>
            ))}
          </select>

          <select
            value={selectedSubActivity}
            onChange={(e) => setSelectedSubActivity(e.target.value)}
            style={{ flex: 1, padding: "8px" }}
            disabled={!selectedActivity}
          >
            <option value="">All Sub-Activities</option>
            {subActivities.map((sa) => (
              <option key={sa.sub_activity_id} value={sa.sub_activity_id}>
                {sa.sub_activity_name}
              </option>
            ))}
          </select>

          <button
            onClick={() => fetchChart1()}
            style={{
              background: "#007bff",
              color: "#fff",
              border: "none",
              padding: "8px 12px",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            Search
          </button>
        </div>

        <div style={{ height: 400 }}>
          {loading1 ? (
            <p style={{ textAlign: "center", marginTop: "150px" }}>Loading...</p>
          ) : (
            <ResponsiveBar data={barData} {...barSettings1} />
          )}
        </div>
      </div>

{/* Chart 2 */}
<div
  style={{
    background: "#fff",
    borderRadius: "12px",
    padding: "25px",
    boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
  }}
>
  <h2 style={{ textAlign: "center", color: "#222", marginBottom: "20px" }}>
    {selectedClub
      ? `Monthly Submissions by ${
          clubs.find((c) => c.enrclub_id === parseInt(selectedClub))?.enr_club_name ||
          "Selected School"
        } (FY ${selectedFY2})`
      : `Month-wise Total Submitted by All Schools (FY ${selectedFY2})`}
  </h2>

  {/* Filter Section */}
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: "15px",
      marginBottom: "25px",
    }}
  >
    {/* 🔹 Row 1: Financial Year + School Search */}
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 2fr",
        gap: "15px",
        alignItems: "end",
      }}
    >
      {/* Financial Year */}
      <div>
        <label
          style={{
            display: "block",
            fontSize: "13px",
            color: "#555",
            marginBottom: "4px",
          }}
        >
          Financial Year
        </label>
        <select
          value={selectedFY2}
          onChange={(e) => setSelectedFY2(e.target.value)}
          style={{
            width: "100%",
            padding: "8px",
            borderRadius: "6px",
            border: "1px solid #ccc",
          }}
        >
          {financialYears.map((fy) => (
            <option key={fy.label} value={fy.label}>
              {fy.label}
            </option>
          ))}
        </select>
      </div>

      {/* Search School */}
      <div style={{ position: "relative" }}>
        <label
          style={{
            display: "block",
            fontSize: "13px",
            color: "#555",
            marginBottom: "4px",
          }}
        >
          Search School
        </label>
        <input
          type="text"
          value={clubSearch}
          onChange={(e) => {
            const val = e.target.value;
            setClubSearch(val);
            if (val.length >= 2) {
              fetchClubs(val);
            } else {
              setClubs([]);
            }
          }}
          placeholder="🔍 Type at least 2 letters..."
          style={{
            width: "100%",
            padding: "8px",
            borderRadius: "6px",
            border: "1px solid #ccc",
          }}
        />
        {clubLoading && (
          <span
            style={{
              position: "absolute",
              right: "10px",
              top: "32px",
              fontSize: "12px",
              color: "#888",
            }}
          >
            Loading...
          </span>
        )}

        {clubs.length > 0 && (
          <select
            value={selectedClub}
            onChange={(e) => setSelectedClub(e.target.value)}
            style={{
              width: "100%",
              marginTop: "6px",
              padding: "8px",
              borderRadius: "6px",
              border: "1px solid #ccc",
              background: "#fafafa",
            }}
          >
            <option value="">All Schools</option>
            {clubs.map((c) => (
              <option key={c.enrclub_id} value={c.enrclub_id}>
                {c.enr_club_name}
              </option>
            ))}
          </select>
        )}
      </div>
    </div>

    {/* 🔹 Row 2: Activity + Search Button */}
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "2fr 1fr",
        gap: "15px",
        alignItems: "end",
      }}
    >
      {/* Activity */}
      <div>
        <label
          style={{
            display: "block",
            fontSize: "13px",
            color: "#555",
            marginBottom: "4px",
          }}
        >
          Select Activity
        </label>
        <select
          value={selectedActivity2}
          onChange={(e) => setSelectedActivity2(e.target.value)}
          style={{
            width: "100%",
            padding: "8px",
            borderRadius: "6px",
            border: "1px solid #ccc",
          }}
        >
          <option value="">All Activities</option>
          {activities.map((a) => (
            <option key={a.activities_id} value={a.activities_id}>
              {a.activity}
            </option>
          ))}
        </select>
      </div>

      {/* Search Button */}
      <div style={{ textAlign: "right" }}>
        <button
          onClick={() => fetchChart2()}
          style={{
            background: "#28a745",
            color: "#fff",
            border: "none",
            padding: "10px 20px",
            borderRadius: "8px",
            cursor: "pointer",
            fontWeight: "500",
            width: "100%",
          }}
        >
          🔍 Search
        </button>
      </div>
    </div>
  </div>

  {/* Chart Section */}
  <div
    style={{
      height: 420,
      background: "#fafafa",
      borderRadius: "10px",
      padding: "10px",
    }}
  >
    {loading2 ? (
      <p style={{ textAlign: "center", marginTop: "150px" }}>Loading...</p>
    ) : (
      <ResponsiveBar data={schoolData} {...barSettings2} />
    )}
  </div>
</div>

    </div>
  );
}
